import { Markdown } from 'tiptap-markdown'
import { Typography } from '@tiptap/extension-typography'
import {
  // Editor,
  Extensions,
  InputRule,
} from '@tiptap/core'
import { StarterKit } from '@tiptap/starter-kit'
import { Highlight } from '@tiptap/extension-highlight'
import { Placeholder } from '@tiptap/extension-placeholder'
import { TaskList } from '@tiptap/extension-task-list'
import { TaskItem } from '@tiptap/extension-task-item'
import { HorizontalRule } from '@tiptap/extension-horizontal-rule'
import { BubbleMenu } from '@tiptap/extension-bubble-menu'
import { UploadImagesPlugin } from '../../plugins/upload-image'
import { UploadVideosPlugin } from '../../plugins/upload-video'
// import { BaseHubBlock } from './extensions/basehub-block/index'
import { UpdatedImage, Kbd } from '@basehub/tiptap-api-utils'
import {
  CollaborationExtensionsOptions,
  getCollaborationExtensions,
} from '../collaboration'
import { Video } from './extensions/video'
// import { SlashCommands } from './extensions/slash-command'
// import { type SlashCommandType } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/slash-command'
// import { BaseHubInlineBlock } from './extensions/basehub-simple-block'
import { CustomLink } from './extensions/link'
import { CodeBlockWithLanguage } from './extensions/code-block'
import { Table } from './extensions/table'
import { Paragraph } from '@tiptap/extension-paragraph'
import { CodeMark } from './extensions/inline-mark'
import { FakeCursor } from './extensions/fake-cursor'

export type BranchCommentExtensionsOptions = {
  collaborationExtensionsOptions: CollaborationExtensionsOptions
  readonly: boolean
  // slashCommand: Pick<
  //   SlashCommandType,
  //   'component' | 'onSelectBlock' | 'richTextId'
  // >
  // onClickBshbInstanceNode: (id: string) => unknown
  // onPasteBshbInstanceNode: (
  //   editor: Editor,
  //   id: string
  // ) => string | undefined | void | false
}

// need to be in sync with richTextExtensionsForTransformations (server side only transformations)
export const getBranchCommentExtensions = (
  options: BranchCommentExtensionsOptions
) => {
  return [
    Paragraph.extend({
      addKeyboardShortcuts() {
        return {
          // prevents creating a new line break when using the create block underneath shortcut
          'Mod-Enter': () => true,
        }
      },
    }),
    StarterKit.configure({
      heading: false,
      history: false,
      horizontalRule: false,
      code: false,
      paragraph: false,
      dropcursor: {
        color: '#FF6C00',
        width: 2,
      },
      bulletList: {
        HTMLAttributes: {
          class: 'list-disc list-outside leading-normal',
        },
      },
      orderedList: {
        HTMLAttributes: {
          class: 'list-decimal list-outside leading-normal',
        },
      },
      listItem: {
        HTMLAttributes: {
          class: '-mb-2 marker:text-dark-muted',
        },
      },
      blockquote: {
        HTMLAttributes: {
          class: 'border-dark-border-solid pl-3 rounded-l-sm',
          style: 'quotes: none',
        },
      },
      codeBlock: false,
    }),
    CodeMark,
    Kbd,
    CodeBlockWithLanguage,
    // patch to fix horizontal rule bug: https://github.com/ueberdosis/tiptap/pull/3859#issuecomment-1536799740
    HorizontalRule.extend({
      addInputRules() {
        return [
          new InputRule({
            find: /^(?:---|—-|___\s|\*\*\*\s)$/,
            handler: ({ state, range }) => {
              const attributes = {}

              const { tr } = state
              const start = range.from
              const end = range.to

              tr.insert(start - 1, this.type.create(attributes) as any).delete(
                tr.mapping.map(start),
                tr.mapping.map(end)
              )
            },
          }),
        ]
      },
    }).configure({
      HTMLAttributes: {
        class: 'my-4 border-t border-dark-border-solid',
      },
    }),
    // Autocomplete,
    Placeholder.configure({
      placeholder: ({ node }) => {
        if (node.type.name === 'heading') return `Heading ${node.attrs.level}`

        return options.readonly
          ? 'No description provided.'
          : 'Describe your changes here...'
      },
      emptyNodeClass: options.readonly ? 'italic' : '',
      includeChildren: true,
      showOnlyWhenEditable: false,
    }),
    // BaseHubBlock.configure({
    //   callbackOnPaste(editor, id) {
    //     return options.onPasteBshbInstanceNode(editor, id)
    //   },
    // }),
    // BaseHubInlineBlock.configure({
    //   callbackOnPaste(editor, id) {
    //     return options.onPasteBshbInstanceNode(editor, id)
    //   },
    // }),
    Highlight.configure({ multicolor: true }),
    CustomVideo,
    BubbleMenu.configure({
      element: document.querySelector('.menu') as HTMLElement,
    }),
    // SlashCommands.configure({
    //   component: options.slashCommand.component,
    //   onSelectBlock: options.slashCommand.onSelectBlock,
    //   richTextId: options.slashCommand.richTextId,
    // }),
    TaskList.configure({
      HTMLAttributes: { class: 'not-prose' },
    }),
    TaskItem.configure({
      HTMLAttributes: {
        class: 'flex items-baseline',
      },
      nested: true,
    }),
    Typography,
    CustomLink,
    CustomImage.configure({
      allowBase64: true,
    }),
    Table.configure({
      resizable: true,
      allowTableNodeSelection: true,
      cellMinWidth: 50,
      handleWidth: 5,
      lastColumnResizable: true,
    }),
    FakeCursor,
    Markdown,
    ...getCollaborationExtensions(options.collaborationExtensionsOptions),
  ] satisfies Extensions
}

const CustomImage = UpdatedImage.extend({
  addProseMirrorPlugins() {
    return [UploadImagesPlugin()]
  },
})
const CustomVideo = Video.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
      },
      height: {
        default: null,
      },
    }
  },
  addProseMirrorPlugins() {
    return [UploadVideosPlugin()]
  },
})
